/**
 * Model kolumn tabeli dodatkowej dla konsumentów spoza karty rekordu (edytor układu kolumn
 * i podgląd w kreatorze formatki): stan odczytu definicji pól, deskryptory kolumn i tytuł
 * tabeli — jedno miejsce zamiast powielania tego samego składania w każdym konsumencie.
 */

import { useMemo } from 'react';
import type { EmbeddedTableColumnDescriptor } from '@saas/shared';

import {
  type AdditionalTableFieldsStatus,
  useAdditionalTableFields,
  useAdditionalTablesList,
} from '@/lib/additional-tables/api';

import { additionalTableDescriptors } from './additional-table-descriptors';

export interface AdditionalTableColumnsModel {
  /** Stan odczytu pól tabeli (`useAdditionalTableFields`). */
  status: AdditionalTableFieldsStatus;
  /** Deskryptory kolumn w kolejności `sortOrder`; pusta tablica, dopóki `status` ≠ `'ready'`. */
  descriptors: EmbeddedTableColumnDescriptor[];
  /** Nazwa tabeli z katalogu (`displayName`); `undefined`, dopóki katalog się nie załaduje albo gdy tabeli brak. */
  title: string | undefined;
  /** Błąd zapytania — tylko przy `status === 'error'`. */
  error?: unknown;
}

/**
 * Pola, deskryptory kolumn i tytuł tabeli dodatkowej po slugu. `descriptors` jest memoizowane
 * po tablicy pól, więc zmienia tożsamość tylko wraz z danymi (bezpieczne w zależnościach hooków).
 */
export function useAdditionalTableColumnsModel(
  tableSlug: string | undefined,
): AdditionalTableColumnsModel {
  const { status, fields, error } = useAdditionalTableFields(tableSlug);
  const tablesQuery = useAdditionalTablesList();
  const descriptors = useMemo(() => additionalTableDescriptors(fields), [fields]);
  const title =
    tableSlug === undefined || tableSlug === ''
      ? undefined
      : tablesQuery.data?.find((table) => table.slug === tableSlug)?.displayName;
  return { status, descriptors, title, error };
}
